import { useTranslation } from "react-i18next"
import { Card } from "../Card"
import { ProjectSettingGroup } from "./ProjectSettingGroup"
import { Point } from "../../types"
import { useState, useEffect } from "react"
import { useQuery } from "@apollo/client"
import { GET_GEOCOORDINATES, GET_PROJECTEDCOORDINATES } from "../../api/QuadriApi"
import { reverseCoordinates, roundCoordinates } from "../../util/projectSettings"
import { EditProjectBoundary, WarningMessage } from "./EditProjectBoundary"
import EditProjectBoundaryModal from "./EditProjectBoundaryModal"
import { projectOriginState } from "../StateSelectors"
import ConnectApi from "../../api/ConnectApi"
import { useRecoilValue } from "recoil"
import { useHistory } from "react-router-dom"

// The card to render Connect project settings
const ImportedProjectSettings = (props: {
  connectProjectId: string
  unitSystem?: string
  boundaryCoordinates?: string
  crsName?: string
  tcpsArea?: Point[]
  tcpsCrsName?: string
  projectEnabled?: boolean
  csib64?: string
}) => {
  const { connectProjectId, boundaryCoordinates, crsName, tcpsArea, tcpsCrsName, projectEnabled, csib64 } = props
  const { t } = useTranslation("config")
  const [ProjectedCoordinates, setProjectedCoordinates] = useState<string>()
  const [updatedBoundaryCoordinates, setUpdatedBoundaryCoordinates] = useState<string>()
  const [showProjectBoundaryModal, setShowProjectBoundaryModal] = useState(false)
  const [boundaryEdited, setBoundaryEdited] = useState(false)
  const [formData, setFormData] = useState<string>()
  const [initialFormData, setInitialFormData] = useState<string>()
  const [formDatatobeSent, setFormDatatoBeSent] = useState<string>()
  const projectOrigin = useRecoilValue(projectOriginState)
  const [connectApi, setConnectApi] = useState<ConnectApi | undefined>(undefined)
  const history = useHistory()
  const {
    data: projectedCoordinatesData,
    error: projectedCoordinatesError,
    loading: projectedCoordinatesIsLoading
  } = useQuery(GET_PROJECTEDCOORDINATES, {
    variables: {
      csib64,
      boundaryCoordinates: reverseCoordinates(boundaryCoordinates ?? "")
    }
  })
  const {
    data: geoCoordinatesData,
    error: geoCoordinatesError,
    loading: geoCoordinatesLoading
  } = useQuery(GET_GEOCOORDINATES, {
    variables: { csib64, projectedCoordinates: formDatatobeSent },
    skip: formDatatobeSent === undefined
  })

  useEffect(() => {
    if (projectedCoordinatesData) {
      const p_coordinates = roundCoordinates(projectedCoordinatesData.getProjectedCoordinates.coordinates)
      setProjectedCoordinates(p_coordinates)
      setFormData(p_coordinates)
      setInitialFormData(p_coordinates)
    }
  }, [csib64, projectedCoordinatesData, projectedCoordinatesError])

  useEffect(() => {
    if (geoCoordinatesData) {
      setUpdatedBoundaryCoordinates(reverseCoordinates(geoCoordinatesData.getGeoCoordinates.coordinates))
      setShowProjectBoundaryModal(false)
    }
    if (geoCoordinatesError) {
      console.log("%cError in getting geo coordinates", "color:red", geoCoordinatesError.message)
      console.log("inital form data", initialFormData)
      setFormDatatoBeSent(undefined)
      return
    }
  }, [csib64, geoCoordinatesData, geoCoordinatesError, initialFormData])

  useEffect(() => {
    if (updatedBoundaryCoordinates) {
      const updateBoundryInConnect = async () => {
        const api = new ConnectApi(projectOrigin)
        setConnectApi(api)
        const updatedBoundryInConnect = await api.updateBoundaryCoordinates(
          connectProjectId,
          updatedBoundaryCoordinates
        )
        setBoundaryEdited(true)
      }
      updateBoundryInConnect()
    }
  }, [projectOrigin, updatedBoundaryCoordinates, connectProjectId])

  useEffect(() => {
    if (boundaryEdited) {
      //reload Config
      console.log("%creloading Config", "color:green")
      setBoundaryEdited(false)
      history.replace("/config")
      window.location.reload()
    }
  }, [boundaryEdited, history])

  const closeModal = () => {
    setShowProjectBoundaryModal(false)
    if (ProjectedCoordinates) {
      setFormData(ProjectedCoordinates)
    }
  }

  const onSubmit = async () => {
    // setShowProjectBoundaryModal(false)
    setFormDatatoBeSent(formData)
  }

  return (
    <div className="row">
      <div className="col-12">
        <Card header={t("connectSettings.header")}>
          <div className="col-12 col-lg-4">
            <p className="text-meta">{t("connectSettings.description")}</p>
          </div>
          <div className="col-12 col-lg-8">
            <EditProjectBoundary
              showProjectBoundaryModal={showProjectBoundaryModal}
              setShowProjectBoundaryModal={setShowProjectBoundaryModal}
              csib64={csib64 as string}
              t={t}
            ></EditProjectBoundary>
            {showProjectBoundaryModal && (
              <EditProjectBoundaryModal
                closeModal={closeModal}
                onSubmit={onSubmit}
                formData={formData}
                setFormData={setFormData}
                initialFormData={initialFormData}
                t={t}
                geoCooedinatesError={geoCoordinatesError}
                geoCoordinatesLoading={geoCoordinatesLoading}
              />
            )}

            <BoundaryItem
              connectProjectId={connectProjectId}
              boundaryCoordinates={boundaryCoordinates}
              mismatch={projectEnabled && !areasMatch(boundaryCoordinates, tcpsArea ? tcpsArea : [])}
              t={t}
              projectedCoordinates={ProjectedCoordinates}
              projectedCoordinatesIsLoading={projectedCoordinatesIsLoading}
            />
            <CoordinateItem
              connectProjectId={connectProjectId}
              crsName={crsName}
              mismatch={projectEnabled && !crsNamesAreEqual(crsName, tcpsCrsName)}
              t={t}
            />
          </div>
        </Card>
      </div>
    </div>
  )
}

const BoundaryItem = (props: {
  connectProjectId: string
  boundaryCoordinates?: string
  mismatch?: boolean
  t: any
  projectedCoordinates?: string
  projectedCoordinatesIsLoading?: boolean
  geoCoordinatesLoading?: boolean
}) => {
  const hasButton = true
  return (
    <ProjectSettingGroup
      name={props.t("connectSettings.projectBoundary")}
      projectSettings={[{ name: props.t("connectSettings.coordinates"), value: props.boundaryCoordinates }]}
      optional={true}
      mismatch={props.mismatch}
      message={
        props.mismatch
          ? props.t("connectSettings.mismatch")
          : props.projectedCoordinatesIsLoading
          ? props.t("connectSettings.loading")
          : props.t("connectSettings.boundaryNotDefined")
      }
      hasButton={hasButton}
      projectedCoordinates={props.projectedCoordinates}
    />
  )
}
const CoordinateItem = (props: { connectProjectId: string; crsName?: string; mismatch?: boolean; t: any }) => (
  <ProjectSettingGroup
    name={props.t("connectSettings.coordinateReferenceSystem")}
    projectSettings={[{ name: props.t("connectSettings.system"), value: props.crsName }]}
    optional={true}
    mismatch={props.mismatch}
    message={props.mismatch ? props.t("connectSettings.mismatch") : props.t("connectSettings.crsNotDefined")}
  />
)

const crsNamesAreEqual = (crsName: string | undefined, tcpsCrsName: string | undefined) => {
  if (crsName === "") {
    crsName = undefined
  }
  if (tcpsCrsName === "") {
    tcpsCrsName = undefined
  }

  return crsName === tcpsCrsName
}

const areasMatch = (boundaryCoordinates: string | undefined, tcpsArea: Point[]) => {
  try {
    const boundaryCoordinatesArray = boundaryCoordinates ? JSON.parse(boundaryCoordinates) : []
    if (boundaryCoordinatesArray.length !== tcpsArea?.length) {
      return false
    }

    const precision = 0.001
    for (let i = 0; i < tcpsArea.length; i++) {
      if (
        Math.abs(boundaryCoordinatesArray[i][0] - tcpsArea[i].latitude) > precision ||
        Math.abs(boundaryCoordinatesArray[i][1] - tcpsArea[i].longitude) > precision
      ) {
        return false
      }
    }
    return true
  } catch (e) {
    return false
  }
}

export default ImportedProjectSettings
